import React from 'react'
import UserOrganizationsProvider, {
  PerPageOption,
  useUserOrganizations,
} from 'obvio/Organizations/UserOrganizationsProvider'
import Collection from 'obvio/Organizations/Collection'
import {obvioRoutes} from 'obvio/Routes'
import {useBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import Layout from 'obvio/user/Layout'
import {useObvioUser} from 'obvio/auth'
import {Redirect} from 'react-router'
import {createRoutesFor} from 'organization/OrganizationProvider'
import Page from 'lib/ui/layout/Page'
import Title from 'lib/ui/PageHeader/Title'
import {useCanCreateOrganization} from 'obvio/Billing/plans'
import NewItemButton from 'lib/ui/PageHeader/NewItemButton'
import {useToggleArray} from 'lib/toggle'
import Button from 'lib/ui/Button'
import styled from 'styled-components'
import Pagination from 'lib/ui/Pagination'

import {
  ToggleViewIcon,
  DesktopPageHeader,
  MobilePageHeader,
  HeaderLink,
} from 'lib/ui/GridCollection'
import Search from 'obvio/Organizations/Search'
import {onUnknownChangeHandler} from 'lib/dom'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'

export default function Organizations() {
  useBreadcrumbs([
    {
      title: 'Organizations',
      url: obvioRoutes.root,
    },
  ])

  return (
    <UserOrganizationsProvider>
      <Content />
    </UserOrganizationsProvider>
  )
}

function Content() {
  const {
    organizations,
    loading,
    page,
    setPage,
    perPage,
    setPerPage,
    lastPage,
    error,
    clearError,
  } = useUserOrganizations()
  const shouldRedirectToOrganization = useShouldRedirectToOrganization()
  const [showingListView, toggleListView] = useToggleArray()

  if (shouldRedirectToOrganization) {
    const firstOrganization = organizations[0]
    const routes = createRoutesFor(firstOrganization)
    return <Redirect to={routes.root} />
  }

  return (
    <Layout>
      <Page>
        <DesktopPageHeader>
          <Title text="Organizations" />
          <Container>
            <Search />
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
            <CreateOrganizationButton />
          </Container>
        </DesktopPageHeader>
        <MobilePageHeader>
          <Title text="Organizations" />
          <CreateOrganizationButton />
          <Container>
            <Search />
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
          </Container>
        </MobilePageHeader>

        <StyledErrorAlert onClose={clearError}>{error}</StyledErrorAlert>

        <Collection showingListView={showingListView} />
        <PaginationWrap>
          <Pagination
            count={lastPage}
            page={page}
            disabled={loading}
            onChange={(nextPage) => {
              setPage(nextPage)
              window.scrollTo(0, 0)
            }}
          />
          <PerPageSelector
            perPage={perPage}
            disabled={loading}
            ariaLabel="select organizations per page"
            onChange={(perPage) => {
              setPage(1)
              setPerPage(perPage)
              window.scrollTo(0, 0)
            }}
          />
        </PaginationWrap>
      </Page>
    </Layout>
  )
}

export function PerPageSelector(props: {
  perPage: PerPageOption
  disabled: boolean
  ariaLabel: string
  onChange: (perPage: PerPageOption) => void
}) {
  const {perPage, disabled, ariaLabel, onChange} = props

  const handleChange = (nextPerPage: PerPageOption) => {
    onChange(nextPerPage)
  }

  return (
    <PerPageLabel>
      Per page
      <PerPageSelect
        value={perPage}
        disabled={disabled}
        onChange={onUnknownChangeHandler(handleChange)}
        aria-label={ariaLabel}
      >
        <option value={12}>12</option>
        <option value={24}>24</option>
        <option value={48}>48</option>
        <option value={100}>100</option>
      </PerPageSelect>
    </PerPageLabel>
  )
}

function CreateOrganizationButton() {
  const canCreate = useCanCreateOrganization()
  if (!canCreate) {
    return (
      <HeaderLink to={obvioRoutes.billing.root} disableStyles>
        <NewItemButton text="Subscribe to Obvio" textCollapsed="Subscribe" />
      </HeaderLink>
    )
  }

  return (
    <HeaderLink to={obvioRoutes.organizations.create} disableStyles>
      <NewItemButton
        text="New Organization"
        aria-label="create new organization"
      />
    </HeaderLink>
  )
}

function useShouldRedirectToOrganization() {
  const user = useObvioUser()
  const {organizations} = useUserOrganizations()

  // If no organizations exist, there's nothing to
  // auto-redirect to.
  const hasNone = organizations.length === 0
  if (hasNone) {
    return false
  }

  // If a user has multiple organizations, lets let them
  // choose which one they'd like to access.
  const hasMultiple = organizations.length > 1
  if (hasMultiple) {
    return false
  }

  // If a user only has one organization, and they don't have a plan,
  // then they can't create any more, so let's just re-direct them.
  if (!user.plan) {
    return true
  }

  // If a user has an active plan, we'll always show the organizations
  // list
  return false
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const StyledErrorAlert = styled(ErrorAlert)`
  margin-top: -${(props) => props.theme.spacing[14]};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-top: -${(props) => props.theme.spacing[2]};
  }
`

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: ${(props) => props.theme.spacing[8]};
  margin-bottom: ${(props) => props.theme.spacing[4]};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${(props) => props.theme.spacing[4]};
  }
`

const PerPageLabel = styled.label`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  gap: ${(props) => props.theme.spacing[2]};
  font-size: 14px;
  line-height: 17px;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    position: static;
  }
`

const PerPageSelect = styled.select`
  border: none;
  border-radius: 3px;
  background: rgba(196, 196, 196, 0.25);
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  font-weight: normal;
  line-height: 17px;
  padding: 3.5px 8px;
`
